import React, { useMemo } from 'react';
import { Popover } from '@headlessui/react';
import { UserIcon } from '@heroicons/react/24/outline';
import { Link, Typography } from '@loomstateorg/shared-frontend';
import AccountDropdown, {
  AccountDropdownLook,
} from '@loomstateorg/shared-frontend/components/commercetools-ui/organisms/account/account-atoms/account-dropdown';
import { useFormat } from '@loomstateorg/shared-frontend/hooks/useFormat';
import { useAccount } from 'frontastic';

export interface Props {
  overlayClassName?: string;
  panelClassName?: string;
  panelPositionClassName?: string;
  dropdownLook?: AccountDropdownLook;
}

const AccountButton = ({
  overlayClassName = 'bg-primary-regular',
  panelClassName = 'bg-site',
  panelPositionClassName = 'left-auto right-[-65px]',
  dropdownLook,
}: Props) => {
  const { account, loggedIn } = useAccount();
  const { formatMessage: formatAccountMessage } = useFormat({ name: 'account' });
  const userName = useMemo(() => {
    return `${account?.salutation ?? formatAccountMessage({ id: 'hello', defaultMessage: 'Hi, ' })} ${
      account?.firstName ?? formatAccountMessage({ id: 'user', defaultMessage: 'User' })
    }`;
  }, [account?.firstName, account?.salutation, formatAccountMessage]);

  const title = account
    ? formatAccountMessage({ id: 'account', defaultMessage: 'Account' })
    : formatAccountMessage({ id: 'sign.in', defaultMessage: 'Login' });
  return (
    <div className="mt-12 h-40" data-testid="account-menu-item">
      <div className="w-fit">
        <div className="hidden md:block lg:hidden">
          <Link link={account ? '/account' : '/login'} title={title} data-testid="account">
            <UserIcon className="h-fit w-28 text-primary-regular" />
          </Link>
        </div>
        {loggedIn ? (
          <Popover as="div" className="relative hidden h-fit lg:flex">
            {({ close }) => (
              <>
                <Popover.Button
                  title={title}
                  className="border-0 bg-transparent p-0 shadow-none outline-none ring-0 [-webkit-tap-highlight-color:transparent] focus:border-transparent focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0 active:border-0 active:outline-none"
                  data-testid="account-toggle"
                >
                  <div className="flex w-fit whitespace-nowrap">
                    <div className="mr-16 hidden w-fit py-2 lg:inline-block">
                      <Typography
                        className="hidden truncate text-16 text-primary-regular lg:block 2xl:text-18"
                        data-testid="account-name"
                      >
                        {userName}
                      </Typography>
                    </div>

                    <div className="border-primary-regular pb-8 hover:border-b-2 active:border-0">
                      <UserIcon className="w-28 text-primary-regular" />
                    </div>
                  </div>
                </Popover.Button>
                <Popover.Overlay className={`fixed inset-0 z-[310] ${overlayClassName} opacity-30`} />
                <Popover.Panel
                  data-component-group="text-container"
                  className={`absolute ${panelPositionClassName} top-55 z-[310] animate-[appearDropdown_0.15s_ease-in-out] ${panelClassName} shadow-400`}
                  data-testid="account-dropdown"
                >
                  <div className="absolute -top-20 left-1/2 z-10 w-31 -translate-x-1/2 overflow-hidden">
                    <div className={`size-21 origin-bottom-left rotate-45 ${panelClassName}`} />
                  </div>
                  <AccountDropdown loggedIn closePopover={close} {...dropdownLook} />
                </Popover.Panel>
              </>
            )}
          </Popover>
        ) : (
          <Link link="/login" title={title} className="relative hidden h-fit lg:flex" data-testid="sign-in">
            <div className="flex w-fit whitespace-nowrap">
              <div className="border-primary-regular pb-8 hover:border-b-2">
                <UserIcon className="w-28 text-primary-regular" />
              </div>
            </div>
          </Link>
        )}
      </div>
    </div>
  );
};

export default AccountButton;
